/**
 * Bridges CCM19 consent into Shopify's Customer Privacy API.
 *
 * CCM19 is the consent UI (see snippets/cookie-consent-latori.liquid); Shopify
 * needs to be told the outcome, otherwise its own pixels, Web Pixels apps and
 * Customer Events stay blocked (or fire without consent) no matter what the
 * visitor picked in the CCM19 banner.
 *
 * Mapping: CCM19 has no public purpose-level API — `CCM.acceptedEmbeddings`
 * only exposes `{id, name}`, and embedding ids differ per CCM19 domain, while
 * the *names* are identical across fischer-wolle.de / .ch / the myshopify
 * domain. So the map below is by embedding name. A Shopify category is granted
 * as soon as one of its embeddings is accepted.
 *
 * CCM19 purposes at the time of writing (for orientation, ids are account-wide):
 *   41ba25c Technisch notwendig · a717ff5 Analyse / Statistiken
 *   cdcbd7c Anzeigen / Ads · 7c19e32 Personalisierung · 6cd2721 Social Media
 * Note the CCM19 config puts Meta Pixel under "Analyse / Statistiken" — it is
 * mapped to `marketing` here regardless, because that is what it does.
 *
 * Keep this list in sync when embeddings are added in the CCM19 admin.
 *
 * Other scripts can also call setTrackingConsent. CCM19's own "Shopify"
 * embedding template does this with every signal set to false when it does
 * not find an embedding named "Shopify Pixel". Shopify fires
 * visitorConsentCollected on each write. This script listens for it and
 * writes the CCM19 state again when the written state differs.
 */
(function () {
  'use strict';

  var CATEGORY_EMBEDDINGS = {
    analytics: [
      'Google Analytics 4',
      'Google Tag Manager',
      'Microsoft Clarity'
    ],
    marketing: [
      'Google Ads Conversion Tracking',
      'Google Ads Remarketing',
      'Google Adsense',
      'Klaviyo',
      'Meta Pixel',
      'Bing / Microsoft',
      'Microsoft',
      'Microsoft Advertising',
      'Microsoft Advertising Remarketing',
      'Microsoft Ads Retargeting'
    ],
    preferences: [
      'Google Maps',
      'YouTube Video'
    ]
  };

  var featuresRequested = false;
  var lastApplied = null;
  var reapplies = 0;
  var MAX_REAPPLIES = 5;

  function normalize(name) {
    return String(name).trim().toLowerCase();
  }

  /**
   * @returns {?Object} Shopify consent object, or null while CCM19 is absent
   *   or the visitor hasn't decided yet (Shopify then keeps its own default).
   */
  function readConsent() {
    var ccm = window.CCM;

    if (!ccm || !ccm.consent) return null;

    var accepted = {};

    (ccm.acceptedEmbeddings || []).forEach(function (embedding) {
      if (embedding && embedding.name) accepted[normalize(embedding.name)] = true;
    });

    function granted(names) {
      return names.some(function (name) {
        return accepted[normalize(name)] === true;
      });
    }

    var marketing = granted(CATEGORY_EMBEDDINGS.marketing);

    return {
      analytics: granted(CATEGORY_EMBEDDINGS.analytics),
      marketing: marketing,
      preferences: granted(CATEGORY_EMBEDDINGS.preferences),
      // Shopify's CCPA category — the same processing the marketing embeddings do.
      sale_of_data: marketing
    };
  }

  /**
   * Skips the call when Shopify already holds exactly this consent, so a plain
   * page view doesn't record a fresh consent every time.
   */
  function isUnchanged(consent) {
    var customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;

    if (!customerPrivacy || typeof customerPrivacy.currentVisitorConsent !== 'function') {
      return false;
    }

    var current = customerPrivacy.currentVisitorConsent() || {};

    return Object.keys(consent).every(function (key) {
      return current[key] === (consent[key] ? 'yes' : 'no');
    });
  }

  function apply() {
    var consent = readConsent();

    if (!consent) return;

    var serialized = JSON.stringify(consent);

    if (serialized === lastApplied) return;

    withCustomerPrivacy(function () {
      if (isUnchanged(consent)) {
        lastApplied = serialized;
        return;
      }

      window.Shopify.customerPrivacy.setTrackingConsent(consent, function (result) {
        if (result && result.error) {
          console.warn('[cookie-consent-latori] setTrackingConsent failed:', result.error);
          return;
        }

        lastApplied = serialized;
      });
    });
  }

  function withCustomerPrivacy(callback) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      callback();
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

    if (featuresRequested) {
      // A load is already in flight; the next CCM19 event re-runs apply().
      return;
    }

    featuresRequested = true;

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      featuresRequested = false;

      if (error) {
        console.warn('[cookie-consent-latori] consent-tracking-api failed to load:', error);
        return;
      }

      callback();
    });
  }

  // ccm19WidgetLoaded fires on every page load (also with consent from a previous
  // visit); ccm19WidgetClosed after the visitor saved or declined; the embedding
  // event covers single embeddings unblocked outside the banner.
  ['ccm19WidgetLoaded', 'ccm19WidgetClosed', 'ccm19EmbeddingAccepted'].forEach(function (name) {
    window.addEventListener(name, apply);
  });

  /**
   * Shopify fires this on every setTrackingConsent call, also from other
   * scripts. The event detail carries the written state. When it differs
   * from the CCM19 state, write the CCM19 state again. The event of our own
   * write matches and returns early, so there is no loop. The counter is a
   * second guard against a script that writes a different state repeatedly.
   */
  document.addEventListener('visitorConsentCollected', function (event) {
    var consent = readConsent();
    var detail = (event && event.detail) || {};

    if (!consent || reapplies >= MAX_REAPPLIES) return;

    var matches =
      detail.analyticsAllowed === consent.analytics &&
      detail.marketingAllowed === consent.marketing &&
      detail.preferencesAllowed === consent.preferences &&
      detail.saleOfDataAllowed === consent.sale_of_data;

    if (matches) return;

    reapplies += 1;
    lastApplied = null;
    apply();
  });

  // In case CCM19 initialised before this script ran.
  apply();
})();
